import { useState } from 'react';
import FocusBearLogoAnimated from 'assets/icons/FocusBearLogoAnimated';
import { BaseButtonLg } from 'components/ui/BaseButton';
import { t } from 'services/i18n.service';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent
} from 'components/ui/Collapsible';
import { ChevronRight } from 'assets/icons/ChevronRight';
import { isProduction } from 'utils/helpers';

interface ErrorBoundaryFallbackProps {
  error?: Error | null;
  correlationId?: string | null;
  messageKey?: string;
}

export default function ErrorBoundaryFallback({
  error,
  correlationId,
  messageKey = 'error_page_message'
}: ErrorBoundaryFallbackProps) {
  const [isDebugOpen, setIsDebugOpen] = useState(!isProduction);

  return (
    <div className='w-screen h-screen bg-secondary-50 dark:bg-secondary-dark-100 text-black dark:text-secondary-100 flex flex-col justify-center items-center ~gap-8/16 ~p-4/8'>
      <FocusBearLogoAnimated />
      <h2 className='w-11/12 md:w-10/12 text-center ~text-sm/xl'>
        {t(messageKey)}
      </h2>
      {correlationId && (
        <div className='flex flex-col items-center gap-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg px-6 py-3'>
          <p className='text-sm text-gray-700 dark:text-gray-300'>
            {t('error_correlation_id')}:
          </p>
          <p className='font-mono font-bold text-lg text-orange-600 dark:text-orange-400 tracking-wider'>
            {correlationId}
          </p>
          <p className='text-xs text-gray-600 dark:text-gray-400 text-center max-w-md'>
            {t(
              'error_page.please_include_this_error_id_when_contacting_support_to_help_us_diagnose_the_issue_faster'
            )}
          </p>
        </div>
      )}
      <BaseButtonLg
        onClick={() => {
          window.location.reload();
        }}
        title={t('error_page.reload_the_page')}
        data-testid='error-reload-btn'
        additionalStyles='~px-6/12'
      />
      {error && !isProduction && (
        <Collapsible
          open={isDebugOpen}
          onOpenChange={setIsDebugOpen}
          className='w-11/12 md:w-10/12 max-w-2xl'>
          <CollapsibleTrigger className='flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors'>
            <span
              className={`transition-transform duration-200 ${isDebugOpen ? 'rotate-90' : ''}`}>
              <ChevronRight styles='w-3 h-3' />
            </span>
            {t('error_debug_info')}
          </CollapsibleTrigger>
          <CollapsibleContent className='mt-4'>
            <div className='bg-gray-100 dark:bg-gray-800 rounded-lg p-4 overflow-auto max-h-64'>
              <p className='font-semibold text-sm mb-2'>
                {error.name}: {error.message}
              </p>
              {error.stack && (
                <pre className='text-xs font-mono whitespace-pre-wrap break-words text-gray-600 dark:text-gray-400'>
                  {error.stack}
                </pre>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}
